import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import Table from "examples/Tables/Table";
import { useState } from "react";
import DeleteDialog from "components/DeleteDialog/DeleteDialog";
import CategoryTableData from "Views/Curriculum/data/categoryTableData";
import ArgonButton from "components/ArgonButton";
import axios from "utils/api";
import { baseURL } from "utils/api";
import { toast } from "react-toastify";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import ArgonInput from "components/ArgonInput";

function Category() {
  const [refresh, setRefresh] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // Add/Edit form dialog state
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("add"); // 'add' | 'edit'
  const [editId, setEditId] = useState(null);
  const [categoryName, setCategoryName] = useState("");
  const [formLoading, setFormLoading] = useState(false);

  // Delete button click handler
  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setDeleteDialog(true);
  };

  // Close dialog
  const handleCloseDialog = () => {
    setDeleteDialog(false);
    setDeleteId(null); // reset selected id
  };

  // Open Add form
  const handleAddClick = () => {
    setFormMode("add");
    setCategoryName("");
    setEditId(null);
    setFormOpen(true);
  };

  // Open Edit form from table
  const handleEditClick = (id, name) => {
    setFormMode("edit");
    setEditId(id);
    setCategoryName(name || "");
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setFormLoading(false);
    setCategoryName("");
    setEditId(null);
  };

  // Submit Add/Edit
  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (!categoryName.trim()) {
      toast.error("Category name cannot be empty");
      return;
    }
    setFormLoading(true);
    if (formMode === "add") {
      axios
        .post(`${baseURL}/categories`, { name: categoryName })
        .then((res) => {
          toast.success(res.data?.msg || "Category added successfully");
          setRefresh((prev) => !prev);
          handleCloseForm();
        })
        .catch((err) => {
          toast.error(err.response?.data?.msg || "Failed to add category");
        })
        .finally(() => setFormLoading(false));
    } else {
      axios
        .put(`${baseURL}/categories/${editId}`, { name: categoryName })
        .then((res) => {
          toast.success(res.data?.msg || "Category updated successfully");
          setRefresh((prev) => !prev);
          handleCloseForm();
        })
        .catch((err) => {
          toast.error(err.response?.data?.msg || "Failed to update category");
        })
        .finally(() => setFormLoading(false));
    }
  };

  const handleConfirmDelete = () => {
    axios
      .delete(`${baseURL}/categories/${deleteId}`)
      .then((res) => {
        toast.success(res.data?.msg || "Category deleted successfully");
        setRefresh((prev) => !prev);
        handleCloseDialog();
      })
      .catch((err) => {
        toast.error(err.response?.data?.msg || "Failed to delete category");
      });
  };

  const { columns, rows, count } = CategoryTableData(handleDeleteClick, handleEditClick, refresh);

  return (
    <ArgonBox>
      <ArgonBox display="flex" justifyContent="space-between" alignItems="center" px={4} mb={3}>
        <ArgonTypography variant="h6" size="small">
          All Category
        </ArgonTypography>
        <ArgonButton variant="contained" color="primary" size="small" onClick={handleAddClick}>
          Add Category
        </ArgonButton>
      </ArgonBox>
      <ArgonBox
        sx={{
          "& .MuiTableRow-root:not(:last-child)": {
            "& td": {
              borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                `${borderWidth[1]} solid ${borderColor}`,
            },
          },
        }}
      >
        {rows.length > 0 ? (
          <Table columns={columns} rows={rows} />
        ) : (
          <ArgonBox textAlign="center" py={4}>
            <ArgonTypography variant="h6" color="text" fontWeight="medium">
              No category found
            </ArgonTypography>
            <ArgonTypography variant="caption" color="secondary">
              Click &apos;Add Category&apos; to create a new category.
            </ArgonTypography>
          </ArgonBox>
        )}
      </ArgonBox>

      {/* Delete Confirmation Dialog */}
      <DeleteDialog
        open={deleteDialog}
        onClose={handleCloseDialog}
        onConfirm={handleConfirmDelete}
        title="Delete Category"
        description="Are you sure you want to delete this category?"
        confirmText="Delete Category"
        cancelText="Cancel"
      />

      {/* Add/Edit Form Dialog */}
      <Dialog
        open={formOpen}
        onClose={handleCloseForm}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            padding: 2,
          },
        }}
      >
        <DialogTitle>
          <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
            <ArgonTypography variant="h5" fontWeight="medium">
              {formMode === "add" ? "Add New Category" : "Edit Category"}
            </ArgonTypography>
            <IconButton onClick={handleCloseForm} size="small">
              <Close />
            </IconButton>
          </ArgonBox>
        </DialogTitle>
        <Divider />
        <form onSubmit={handleFormSubmit}>
          <DialogContent>
            <ArgonBox mb={2}>
              <ArgonTypography variant="caption" fontWeight="medium" color="text">
                Category Name *
              </ArgonTypography>
              <ArgonInput
                type="text"
                placeholder="Enter category name"
                value={categoryName}
                onChange={(e) => setCategoryName(e.target.value)}
                fullWidth
                required
              />
            </ArgonBox>
          </DialogContent>
          <Divider />
          <DialogActions>
            <ArgonButton onClick={handleCloseForm} color="secondary" variant="outlined">
              Cancel
            </ArgonButton>
            <ArgonButton type="submit" color="primary" variant="contained" disabled={formLoading}>
              {formLoading ? "Saving..." : formMode === "add" ? "Add" : "Update"}
            </ArgonButton>
          </DialogActions>
        </form>
      </Dialog>
    </ArgonBox>
  );
}

export default Category;
